import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { RepositoryAnalysis } from '../types/analysis.types';
import { formatCount } from '../../utils/formatters';

const LANGUAGE_COLORS = [
  'bg-yellow-400',
  'bg-blue-500',
  'bg-emerald-500',
  'bg-purple-500',
  'bg-orange-400',
  'bg-pink-400',
];

const ACTIVE_COLOR = '#14b8a6';

interface ProjectListProps {
  projects: RepositoryAnalysis[];
  selectedProject?: RepositoryAnalysis | null;
  onSelectProject?: (project: RepositoryAnalysis) => void;
}

export const ProjectList: React.FC<ProjectListProps> = ({
  projects,
  selectedProject,
  onSelectProject,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  if (projects.length <= 1) return null;

  return (
    <div className="border-t border-slate-100 dark:border-slate-700/40">
      <button
        onClick={() => setIsCollapsed((c) => !c)}
        className="flex items-center gap-2 px-5 pt-4 pb-2 w-full hover:bg-slate-50/60 dark:hover:bg-slate-700/15 transition-colors"
      >
        <p className="text-xs font-bold uppercase tracking-widest text-secondary/50">Projects</p>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700/50 text-secondary/50 tabular-nums leading-none">
          {projects.length}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-secondary/40 ml-auto transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`}
        />
      </button>
      <div className="flex flex-col divide-y divide-slate-100 dark:divide-slate-700/40">
        {projects.map((project) => {
          if (isCollapsed && project.projectIndex !== -1) return null;
          const isActive = selectedProject ? sameProject(project, selectedProject) : false;
          const projectType = project.repositoryDetails?.projectType;
          const gapCount = project.gaps?.length ?? 0;
          const langs = project.languages?.slice(0, 5) ?? [];
          const filesLabel = `${formatCount(project.filesCount)} files`;
          const desc = project.projectDescription || project.repositoryDetails?.description;

          const isFirst = project.projectIndex === -1;

          return (
            <button
              key={projectKey(project)}
              onClick={() => {
                if (isFirst) setIsCollapsed((c) => !c);
                onSelectProject?.(project);
              }}
              className={`flex items-center gap-4 px-5 py-4 text-left w-full transition-all duration-150
                ${
                  isActive
                    ? 'bg-slate-50 dark:bg-slate-700/30'
                    : 'hover:bg-slate-50/60 dark:hover:bg-slate-700/15'
                }`}
              style={{
                borderLeft: isActive ? `3px solid ${ACTIVE_COLOR}` : '3px solid transparent',
              }}
            >
              <div className="flex-1 min-w-0">
                {/* Name + version + path */}
                <div className="flex items-baseline gap-2 min-w-0">
                  <span
                    className="text-[20px] font-extrabold font-mono truncate leading-snug transition-colors duration-150"
                    style={{ color: isActive ? ACTIVE_COLOR : undefined }}
                  >
                    {project.repositoryName || (
                      <span className="text-secondary/30 italic font-normal text-sm">Unnamed</span>
                    )}
                  </span>
                  {project.projectIndex === -1 && (
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full border border-violet-400/40 bg-violet-500/15 text-violet-600 dark:text-violet-300 shrink-0 self-center">
                      Aggregate
                    </span>
                  )}
                  {project.projectIndex === 0 && (
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full border border-teal-400/40 bg-teal-500/15 text-teal-600 dark:text-teal-300 shrink-0 self-center">
                      Repo Root
                    </span>
                  )}
                  {project.projectVersion && (
                    <span className="text-xs text-secondary/40 shrink-0 font-normal self-center">
                      v{project.projectVersion}
                    </span>
                  )}
                </div>

                {/* Path */}
                {project.projectPath && project.projectPath !== '.' && (
                  <div className="flex items-center mt-0.5 font-mono">
                    {project.projectPath.split('/').map((segment, i, arr) => {
                      const isLast = i === arr.length - 1;
                      return (
                        <span key={i} className="flex items-center">
                          {i > 0 && (
                            <span className="text-[11px] text-secondary/25 mx-0.5 leading-none select-none">
                              /
                            </span>
                          )}
                          <span
                            className={
                              isLast
                                ? 'text-[11px] font-semibold text-secondary/70 leading-none'
                                : 'text-[11px] text-secondary/35 leading-none'
                            }
                          >
                            {segment}
                          </span>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Description */}
                <p
                  className={`text-xs mt-1 truncate leading-relaxed ${desc ? 'text-secondary/55' : 'text-secondary/25 italic'}`}
                >
                  {desc ?? 'No description'}
                </p>

                {/* Language bar + names */}
                {langs.length > 0 && (
                  <div className="mt-2">
                    <div className="flex w-full h-1.5 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-700/50">
                      {langs.map((lang, i) => {
                        const pct = typeof lang === 'object' ? (lang.percentage ?? 0) : 0;
                        return (
                          <div
                            key={i}
                            className={`${LANGUAGE_COLORS[i % LANGUAGE_COLORS.length]} h-full`}
                            style={{ width: `${pct}%` }}
                          />
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-4 mt-1.5 flex-wrap">
                      {langs.map((lang, i) => {
                        const name = typeof lang === 'string' ? lang : lang.name;
                        const pct =
                          typeof lang === 'object' && lang.percentage ? lang.percentage : null;
                        return (
                          <span key={i} className="flex items-center gap-1.5">
                            <span
                              className={`w-2 h-2 rounded-full shrink-0 ${LANGUAGE_COLORS[i % LANGUAGE_COLORS.length]}`}
                            />
                            <span className="text-xs font-bold text-secondary/65">{name}</span>
                            {pct !== null && (
                              <span className="text-[11px] text-secondary/40">{pct}%</span>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Meta chips */}
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  {projectType && (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700/50 text-secondary/60 capitalize">
                      {projectType.replace(/_/g, ' ')}
                    </span>
                  )}
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700/50 text-secondary/45 tabular-nums">
                    {filesLabel}
                  </span>
                  {(project.strengths?.length ?? 0) > 0 && (
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded tabular-nums text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-400/10">
                      {project.strengths!.length}{' '}
                      {project.strengths!.length === 1 ? 'strength' : 'strengths'}
                    </span>
                  )}
                  {gapCount > 0 && (
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded tabular-nums text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-400/10">
                      {gapCount} {gapCount === 1 ? 'gap' : 'gaps'}
                    </span>
                  )}
                  {(project.recommendations?.length ?? 0) > 0 && (
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded tabular-nums text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-400/10">
                      {project.recommendations!.length} recommendations
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

function sameProject(a: RepositoryAnalysis, b: RepositoryAnalysis): boolean {
  if (a.projectIndex !== undefined && b.projectIndex !== undefined) {
    return a.projectIndex === b.projectIndex;
  }
  if (a.projectPath && b.projectPath) {
    return a.projectPath === b.projectPath;
  }
  return a.repositoryName === b.repositoryName;
}

function projectKey(project: RepositoryAnalysis): string {
  if (project.projectIndex !== undefined) return `project-${project.projectIndex}`;
  if (project.projectPath) return `project-${project.projectPath}`;
  return project.repositoryName;
}
